import { useTranslation } from 'react-i18next';
import { FaSync as RefreshIcon } from 'react-icons/fa';
import { Banner, Button, Progress } from '@knack/asterisk-react';

import { useCountdown } from '@/hooks/useCountdown';

interface TwoFaCountdownProps {
  expiresAt?: number; // Unix timestamp
  onRefresh: () => void;
  isRefreshing?: boolean;
}

// Default QR code lifetime: 5 minutes (300 seconds)
// This is hardcoded in the Server, under the variable `UnverifiedTotpExpirationMilliseconds`, and it's not returned by the API.
// @see Server/src/app/constants/live-app.js
const DEFAULT_QR_LIFETIME_SECONDS = 300;

/**
 * Displays a countdown timer and progress bar for a 2FA QR code expiration, with an option to refresh when expired.
 *
 * Renders a warning banner and refresh button when the QR code has expired, or shows the remaining time and progress bar while active. Returns null if no expiration time is provided.
 *
 * @param expiresAt - Unix timestamp indicating when the QR code expires.
 * @param onRefresh - Callback invoked to refresh the QR code.
 * @param isRefreshing - Indicates if the refresh action is currently in progress.
 *
 * @returns The countdown UI for the QR code expiration, or null if {@link expiresAt} is not provided.
 */
export function TwoFaCountdown({
  expiresAt,
  onRefresh,
  isRefreshing = false
}: TwoFaCountdownProps) {
  const [t] = useTranslation();

  const { timeLeft, isExpired, progressPercentage, formatTime } = useCountdown({
    expiresAt,
    defaultDuration: DEFAULT_QR_LIFETIME_SECONDS
  });

  // Return null if no expiresAt is provided (maintains original behavior)
  if (!expiresAt) {
    return null;
  }

  return (
    <div
      className="mb-4 h-24 w-full"
      data-testid={isExpired ? 'twofa-countdown-expired' : 'twofa-countdown-active'}
    >
      {isExpired ? (
        <div className="flex size-full flex-col justify-center">
          <Banner intent="warning" className="mb-2 w-full">
            <Banner.Message>{t('components.two_factor_auth.setup.qr_expired')}</Banner.Message>
          </Banner>
          <Button
            type="button"
            intent="secondary"
            onClick={onRefresh}
            isLoading={isRefreshing}
            className="w-full"
            data-testid="twofa-refresh-button"
          >
            <RefreshIcon className="mr-2 h-4 w-4" />
            {t('components.two_factor_auth.setup.refresh_qr')}
          </Button>
        </div>
      ) : (
        <div className="flex size-full flex-col justify-center">
          <div className="text-muted-foreground mb-3 text-start text-sm">
            {t('components.two_factor_auth.setup.expires_in')}{' '}
            <span
              className={`font-mono font-semibold ${timeLeft < 60 ? 'text-red-600' : 'text-emphasis'}`}
              data-testid="twofa-time-remaining"
            >
              {formatTime(timeLeft)}
            </span>
          </div>

          <Progress
            intent={timeLeft < 60 ? 'destructive' : 'brand'}
            size="sm"
            value={progressPercentage}
            data-testid="twofa-progress-bar"
          />
        </div>
      )}
    </div>
  );
}
